/**
 * Collapsed "History" section for the question editor.
 *
 * On expand, fetches the revision summaries via the injected `historyApi`,
 * plus the snapshot of the latest revision — that snapshot is the diff
 * baseline ("Live"). We deliberately do NOT diff against the in-memory form
 * state because the form's shape (QuestionFormData) drops fields like
 * `importStatus` and stores cleared fields as null vs the document's
 * undefined, both of which cause false positives in the diff.
 *
 * `historyApi` is injected per-app — shared-ui doesn't import any concrete
 * API client. See docs/FeatureDesigns/question-version-history.md.
 */

import { useState, useCallback } from 'react';
import { QuestionRevisionDiff } from './QuestionRevisionDiff';
import { formatDateTime24 } from '../components/dateFormat';
import type { QuestionHistoryApi, QuestionRevisionSummary, QuestionRevision } from './types';
import './QuestionHistoryPanel.css';

interface QuestionHistoryPanelProps {
  questionId: string;
  historyApi: QuestionHistoryApi;
  /** Callback fired after a successful restore so the parent can reload. */
  onRestored?: () => void;
  /** Hide the restore action — used on the read-only view, where restore
   *  belongs to edit mode. Browsing revisions and diffs stays available. */
  readOnly?: boolean;
}

const ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Edited',
  publish: 'Published',
  unpublish: 'Unpublished',
  restore: 'Restored',
  delete: 'Deleted',
  /* Not "Created": the question already existed, and this row records the state
     it was in when history began — for anything seeded, imported, or older than
     version history itself. */
  baseline: 'Existing state',
};

function formatWhen(iso: string): string {
  try {
    return formatDateTime24(iso);
  } catch {
    return iso;
  }
}

function formatActor(r: { changedBy: string; changedByName?: string }): string {
  return r.changedByName ?? r.changedBy;
}

export function QuestionHistoryPanel({
  questionId,
  historyApi,
  onRestored,
  readOnly = false,
}: QuestionHistoryPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revisions, setRevisions] = useState<QuestionRevisionSummary[] | null>(null);
  const [latestSnapshot, setLatestSnapshot] = useState<Record<string, unknown> | null>(null);
  const [selected, setSelected] = useState<QuestionRevision | null>(null);
  const [restoring, setRestoring] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await historyApi.list(questionId);
      setRevisions(res.revisions);
      // Fetch the latest revision's full snapshot as the diff baseline.
      // This is the persisted "live" state — not the in-progress form state.
      if (res.revisions.length > 0) {
        const latest = await historyApi.get(questionId, res.revisions[0].seq);
        setLatestSnapshot(latest.revision.snapshot);
      } else {
        setLatestSnapshot(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [historyApi, questionId]);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && revisions === null) {
      void loadList();
    }
  };

  const handleSelect = async (seq: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await historyApi.get(questionId, seq);
      setSelected(res.revision);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!selected) return;
    const note = window.prompt(
      `Restore version ${selected.seq}? This creates a new revision with the snapshot's content. Optional note:`,
      ''
    );
    if (note === null) return; // cancelled
    setRestoring(true);
    setError(null);
    try {
      await historyApi.restore(questionId, selected.seq, note || undefined);
      setSelected(null);
      await loadList();
      onRestored?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="qh-panel">
      <button type="button" className="qh-panel-toggle" onClick={handleToggle}>
        <span className={`qh-chevron ${expanded ? 'expanded' : ''}`}>▸</span>
        History
        {revisions !== null && <span className="qh-count"> ({revisions.length})</span>}
      </button>

      {expanded && (
        <div className="qh-panel-body">
          {loading && <div className="qh-loading">Loading…</div>}
          {error && <div className="qh-error">Error: {error}</div>}

          {revisions !== null && revisions.length === 0 && !loading && (
            <div className="qh-empty">No history yet — first edit will start the trail.</div>
          )}

          {revisions !== null && revisions.length > 0 && (
            <div className="dt-scroll">
              <table className="qh-table">
                <thead>
                  <tr>
                    <th>Ver</th>
                    <th>When</th>
                    <th>Who</th>
                    <th>Action</th>
                    <th>Changed fields</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {revisions.map(r => (
                    <tr
                      key={r.seq}
                      className={selected?.seq === r.seq ? 'qh-row-selected' : ''}
                      onClick={() => handleSelect(r.seq)}
                    >
                      <td>{r.seq}</td>
                      <td>{formatWhen(r.changedAt)}</td>
                      <td className="qh-actor">{formatActor(r)}</td>
                      <td>
                        <span className={`qh-action qh-action-${r.action}`}>
                          {ACTION_LABELS[r.action] ?? r.action}
                        </span>
                        {r.parentSeq !== undefined && <span className="qh-parent"> ← v{r.parentSeq}</span>}
                      </td>
                      <td className="qh-fields">{r.changedFields.join(', ') || '—'}</td>
                      <td className="qh-note">{r.changeNote ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selected && (
            <div className="qh-detail">
              <div className="qh-detail-header">
                <div>
                  <strong>Version {selected.seq}</strong> — {ACTION_LABELS[selected.action] ?? selected.action}
                  {' · '}{formatWhen(selected.changedAt)}
                  {' · '}{formatActor(selected)}
                </div>
                <div className="qh-detail-actions">
                  {!readOnly && revisions?.find(r => r.seq === selected.seq)?.canRestore && selected.action !== 'delete' && (
                    <button
                      type="button"
                      className="qh-btn qh-btn-restore"
                      onClick={handleRestore}
                      disabled={restoring}
                    >
                      {restoring ? 'Restoring…' : 'Restore this version'}
                    </button>
                  )}
                  <button type="button" className="qh-btn" onClick={() => setSelected(null)}>
                    Close
                  </button>
                </div>
              </div>
              <QuestionRevisionDiff
                before={selected.snapshot}
                after={latestSnapshot ?? {}}
                beforeLabel={`v${selected.seq}`}
                afterLabel={revisions && revisions.length > 0 ? `v${revisions[0].seq} (live)` : 'Live'}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
